import { ApiResponse } from "src/models";
import {
  DataRequestEvent,
  DataRequestRecord,
  DistrictStudentMatch,
  PrivacyImpact,
  PrivacyImpactChange,
  PrivacySettingsView,
  PrivacyVideos,
  UpdatePrivacySettingsPayload,
} from "src/models/hierarchy";
import {
  DistrictHistoryCategory,
  HistoryEntry,
  HistoryPageArgs,
} from "src/models/history";
import { Page, toQueryString } from "src/models/staff";
import { apiSlice } from "src/store/apiSlice";

const base = "/api/account/v1";

/** A list the server sends as `data.<key>` (BusinessResponse) or as `data` itself. */
const listOf = <T>(data: unknown, key: string): T[] => {
  if (Array.isArray(data)) return data as T[];
  const list = (data as Record<string, unknown> | undefined)?.[key];
  return Array.isArray(list) ? (list as T[]) : [];
};

/** A request the server sends as `data.request` or as `data` itself. */
const requestOf = (data: unknown): DataRequestRecord => {
  const wrapped = (data as { request?: DataRequestRecord } | undefined)
    ?.request;
  return wrapped ?? (data as DataRequestRecord);
};

export const privacyApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPrivacySettings: builder.query<PrivacySettingsView, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/privacy-settings`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<PrivacySettingsView>) => r.data,
      providesTags: (_r, _e, districtId) => [
        { type: "Privacy", id: districtId },
      ],
    }),
    /** How many records a data-removing change affects (R11, R12); always fetched fresh. */
    getPrivacyImpact: builder.query<
      PrivacyImpact,
      { districtId: string; change: PrivacyImpactChange }
    >({
      query: ({ districtId, change }) => ({
        url: `${base}/districts/${districtId}/privacy-settings/impact${toQueryString(
          { change },
        )}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<PrivacyImpact>) => r.data,
      keepUnusedDataFor: 0,
    }),
    /** Videos still held and when the last expires (R13). */
    getPrivacyVideos: builder.query<PrivacyVideos, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/privacy-settings/videos`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<PrivacyVideos>) => r.data,
      providesTags: (_r, _e, districtId) => [
        { type: "Privacy", id: districtId },
      ],
    }),
    /** Queues deletion of every held video (FR-002); 409 VIDEO_ENABLED while video is on. */
    deletePrivacyVideos: builder.mutation<{ jobId: string }, string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/privacy-settings/videos/delete`,
        method: "POST",
        body: { confirmation: "delete" },
      }),
      transformResponse: (r: ApiResponse<{ jobId: string }>) => r.data,
      invalidatesTags: (_r, _e, districtId) => [
        { type: "Privacy", id: districtId },
        { type: "DistrictHistory", id: districtId },
      ],
    }),
    /**
     * Privacy changes and their clean-ups, newest first: the district history filtered to
     * `privacy,cleanup` by default (R21). Pages fit `HistoryTimeline`'s `usePage`.
     */
    getPrivacyHistory: builder.query<
      Page<HistoryEntry>,
      HistoryPageArgs & {
        districtId: string;
        category?: DistrictHistoryCategory[];
      }
    >({
      query: ({ districtId, category, limit, offset }) => ({
        url: `${base}/districts/${districtId}/history${toQueryString({
          category: (category ?? ["privacy", "cleanup"]).join(","),
          limit,
          offset,
        })}`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<{ items: HistoryEntry[]; total: number }>,
        _meta,
        { limit, offset },
      ) => ({
        items: r.data.items,
        total: r.data.total,
        limit: limit ?? r.data.items.length,
        offset: offset ?? 0,
      }),
      providesTags: (_r, _e, { districtId }) => [
        { type: "DistrictHistory", id: districtId },
      ],
    }),
    updatePrivacySettings: builder.mutation<
      PrivacySettingsView,
      { districtId: string; body: UpdatePrivacySettingsPayload }
    >({
      query: ({ districtId, body }) => ({
        url: `${base}/districts/${districtId}/privacy-settings`,
        method: "PUT",
        body,
      }),
      transformResponse: (r: ApiResponse<PrivacySettingsView>) => r.data,
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "Districts", id: districtId },
        { type: "Privacy", id: districtId },
        { type: "DistrictHistory", id: districtId },
        "Students",
      ],
    }),
    listDataRequests: builder.query<DataRequestRecord[], string>({
      query: (districtId) => ({
        url: `${base}/districts/${districtId}/data-requests`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ requests: DataRequestRecord[] }>) =>
        r.data.requests,
      providesTags: (_r, _e, districtId) => [
        { type: "DataRequests", id: districtId },
      ],
    }),
    getDataRequest: builder.query<
      DataRequestRecord,
      { districtId: string; requestId: string }
    >({
      query: ({ districtId, requestId }) => ({
        url: `${base}/districts/${districtId}/data-requests/${requestId}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ request: DataRequestRecord }>) =>
        r.data.request,
      providesTags: (_r, _e, { requestId }) => [
        { type: "DataRequests", id: requestId },
      ],
    }),
    createDataRequest: builder.mutation<
      DataRequestRecord,
      {
        districtId: string;
        body: {
          type: "export" | "delete";
          scope: "district" | "student";
          studentId?: string;
          confirmation?: string;
        };
      }
    >({
      query: ({ districtId, body }) => ({
        url: `${base}/districts/${districtId}/data-requests`,
        method: "POST",
        body,
      }),
      transformResponse: (r: ApiResponse<{ request: DataRequestRecord }>) =>
        r.data.request,
      invalidatesTags: (_r, _e, { districtId }) => [
        { type: "DataRequests", id: districtId },
      ],
    }),
    /** Name or SIS id lookup for the request dialog: at most 20 active or inactive students (FR-017). */
    searchDistrictStudents: builder.query<
      DistrictStudentMatch[],
      { districtId: string; q: string; limit?: number }
    >({
      query: ({ districtId, q, limit = 20 }) => ({
        url: `${base}/districts/${districtId}/students/search${toQueryString({
          q,
          limit,
        })}`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<
          { students?: DistrictStudentMatch[] } | DistrictStudentMatch[]
        >,
      ) => listOf(r.data, "students"),
      keepUnusedDataFor: 30,
    }),
    /**
     * A 15-minute download link for a completed, unexpired export (FR-023). Created on each click
     * and recorded as `download-link-created`, so it is a mutation and never cached.
     */
    downloadDataRequest: builder.mutation<
      { url: string },
      { districtId: string; requestId: string }
    >({
      query: ({ districtId, requestId }) => ({
        url: `${base}/districts/${districtId}/data-requests/${requestId}/download`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<{ url: string }>) => r.data,
      invalidatesTags: (_r, _e, { requestId }) => [
        { type: "DataRequests", id: `events:${requestId}` },
      ],
    }),
    /** Cancels a pending request; 409 `NOT_CANCELLABLE` once it has started (FR-021). */
    cancelDataRequest: builder.mutation<
      DataRequestRecord,
      { districtId: string; requestId: string }
    >({
      query: ({ districtId, requestId }) => ({
        url: `${base}/districts/${districtId}/data-requests/${requestId}/cancel`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<{ request?: DataRequestRecord }>) =>
        requestOf(r.data),
      invalidatesTags: (_r, _e, { districtId, requestId }) => [
        { type: "DataRequests", id: districtId },
        { type: "DataRequests", id: requestId },
        { type: "DataRequests", id: `events:${requestId}` },
      ],
    }),
    /** Runs a failed request again from its last completed step; 409 `NOT_RETRYABLE` otherwise (FR-021). */
    retryDataRequest: builder.mutation<
      DataRequestRecord,
      { districtId: string; requestId: string }
    >({
      query: ({ districtId, requestId }) => ({
        url: `${base}/districts/${districtId}/data-requests/${requestId}/retry`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<{ request?: DataRequestRecord }>) =>
        requestOf(r.data),
      invalidatesTags: (_r, _e, { districtId, requestId }) => [
        { type: "DataRequests", id: districtId },
        { type: "DataRequests", id: requestId },
        { type: "DataRequests", id: `events:${requestId}` },
      ],
    }),
    /** Who created, downloaded, cancelled or retried a request, and what ran, oldest first (FR-020). */
    getDataRequestEvents: builder.query<
      DataRequestEvent[],
      { districtId: string; requestId: string }
    >({
      query: ({ districtId, requestId }) => ({
        url: `${base}/districts/${districtId}/data-requests/${requestId}/events`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<{ events?: DataRequestEvent[] } | DataRequestEvent[]>,
      ) => listOf(r.data, "events"),
      providesTags: (_r, _e, { requestId }) => [
        { type: "DataRequests", id: `events:${requestId}` },
      ],
    }),
  }),
});

export const {
  useGetPrivacySettingsQuery,
  useLazyGetPrivacyImpactQuery,
  useGetPrivacyVideosQuery,
  useDeletePrivacyVideosMutation,
  useGetPrivacyHistoryQuery,
  useUpdatePrivacySettingsMutation,
  useListDataRequestsQuery,
  useGetDataRequestQuery,
  useLazyGetDataRequestQuery,
  useCreateDataRequestMutation,
  useSearchDistrictStudentsQuery,
  useDownloadDataRequestMutation,
  useCancelDataRequestMutation,
  useRetryDataRequestMutation,
  useGetDataRequestEventsQuery,
} = privacyApiSlice;
